import { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { ref as storageRef, uploadBytes } from 'firebase/storage';
import { Upload, Loader2, ArrowRight, X, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { storage } from '@/lib/firebase';
import { useAuth, useRole } from '@/features/auth/AuthProvider';
import { canViewCost } from '@/types/schemas';
import { toast } from '@/components/ui/toaster';

// ── Field definitions ──────────────────────────────────────────────────────
type FieldDef = { key: string; label: string; required: boolean };

function getDatahubFields(showCost: boolean): FieldDef[] {
  return [
    { key: 'itemName', label: 'Item Name', required: true },
    { key: 'sku', label: 'SKU', required: false },
    { key: 'category', label: 'Category', required: true },
    { key: 'brand', label: 'Brand', required: false },
    { key: 'subBrand', label: 'Sub Brand', required: false },
    { key: 'unit', label: 'Unit', required: false },
    { key: 'quantity', label: 'Quantity / Stock', required: false },
    { key: 'sellingPrice', label: 'Selling Price', required: false },
    ...(showCost ? [{ key: 'purchasePrice', label: 'Purchase Price', required: false }] : []),
    { key: 'lowStockThreshold', label: 'Low Stock Threshold', required: false },
    { key: 'length', label: 'Length', required: false },
    { key: 'width', label: 'Width', required: false },
    { key: 'thickness', label: 'Thickness', required: false },
  ];
}

const CUSTOMER_FIELDS: FieldDef[] = [
  { key: 'name',    label: 'Customer Name', required: true  },
  { key: 'phone',   label: 'Phone',         required: true  },
  { key: 'email',   label: 'Email',         required: false },
  { key: 'address', label: 'Address',       required: false },
];

const NONE = '__none__';

export function ExcelImportButton({ type }: { type: 'datahub' | 'customers' }) {
  const { user } = useAuth();
  const role = useRole();
  const showCost = canViewCost(role);
  const inputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<'idle' | 'mapping' | 'uploading'>('idle');
  const [file, setFile] = useState<File | null>(null);
  const [columns, setColumns] = useState<string[]>([]);
  // mapping: fieldKey → excelColumn
  const [mapping, setMapping] = useState<Record<string, string>>({});

  const fields = type === 'datahub' ? getDatahubFields(showCost) : CUSTOMER_FIELDS;

  // ── Step 1: read headers from Excel ──────────────────────────────────────
  function handleFileSelect(f: File) {
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1 });
        const firstRow = rows[0];
        const headers = Array.isArray(firstRow)
          ? (firstRow as unknown[]).map((h) => String(h ?? '').trim()).filter(Boolean)
          : [];
        setColumns(headers);

        // Auto-map: try to match column names to field keys intelligently
        const autoMap: Record<string, string> = {};
        fields.forEach((field) => {
          const match = headers.find((h) => {
            const norm = h.toLowerCase().replace(/[\s_-]/g, '');
            const key = field.key.toLowerCase();
            const label = field.label.toLowerCase().replace(/[\s_/-]/g, '');
            return norm === key || norm === label ||
              norm.includes(key) || key.includes(norm);
          });
          if (match) autoMap[field.key] = match;
        });
        setMapping(autoMap);
        setStep('mapping');
      } catch {
        toast({ title: 'Could not read file', description: 'Make sure it is a valid .xlsx file.', variant: 'destructive' });
      }
    };
    reader.readAsArrayBuffer(f);
  }

  // ── Step 2: upload with mapping metadata ─────────────────────────────────
  async function handleUpload() {
    if (!user || !file) return;

    // Validate required fields are mapped
    const missing = fields.filter((f) => f.required && !mapping[f.key]);
    if (missing.length > 0) {
      toast({
        title: 'Missing required mappings',
        description: `Please map: ${missing.map((f) => f.label).join(', ')}`,
        variant: 'destructive',
      });
      return;
    }

    setStep('uploading');
    try {
      const path = `imports/${type}/${user.uid}/${Date.now()}_${file.name}`;
      // Encode mapping as custom metadata (JSON string)
      await uploadBytes(storageRef(storage, path), file, {
        contentType: file.type,
        customMetadata: {
          uploadedBy: user.uid,
          type,
          columnMapping: JSON.stringify(mapping),
        },
      });
      toast({
        title: 'Import started',
        description: 'Processing in the background. Refresh in a moment.',
        variant: 'success',
      });
      handleClose();
    } catch (err) {
      toast({ title: 'Upload failed', description: String(err), variant: 'destructive' });
      setStep('mapping');
    }
  }

  function handleClose() {
    setStep('idle');
    setFile(null);
    setColumns([]);
    setMapping({});
  }

  const mappedCount = Object.values(mapping).filter((v) => v && v !== NONE).length;

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFileSelect(f);
          e.target.value = '';
        }}
      />

      <Button
        variant="outline"
        onClick={() => inputRef.current?.click()}
        disabled={step === 'uploading'}
      >
        {step === 'uploading' ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Upload className="h-4 w-4" />
        )}
        Import Excel
      </Button>

      {/* ── Column Mapping Dialog ── */}
      <Dialog open={step === 'mapping'} onOpenChange={(o) => { if (!o) handleClose(); }}>
        <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Map your columns</DialogTitle>
            <DialogDescription>
              Match your Excel columns to the correct fields. Required fields are marked with *.
            </DialogDescription>
          </DialogHeader>

          {/* File info */}
          <div className="flex items-center gap-2 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
            <span className="truncate font-medium text-foreground">{file?.name}</span>
            <span className="shrink-0">· {columns.length} columns found</span>
          </div>

          {/* Mapping rows */}
          <div className="space-y-2">
            {fields.map((field) => (
              <div key={field.key} className="grid grid-cols-2 items-center gap-3">
                {/* Field label */}
                <div className="text-sm">
                  {field.label}
                  {field.required && <span className="ml-1 text-destructive">*</span>}
                </div>
                {/* Column selector */}
                <Select
                  value={mapping[field.key] ?? NONE}
                  onValueChange={(v) =>
                    setMapping((prev) => ({
                      ...prev,
                      [field.key]: v === NONE ? '' : v,
                    }))
                  }
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="— skip —" />
                  </SelectTrigger>
                  <SelectContent className="max-h-48 overflow-y-auto">
                    <SelectItem value={NONE}>— skip —</SelectItem>
                    {columns.map((col) => (
                      <SelectItem key={col} value={col}>{col}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>

          {/* Arrow visual */}
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground py-1">
            <span>Your Excel columns</span>
            <ArrowRight className="h-3.5 w-3.5" />
            <span>CHHABIGHAR fields</span>
            <Badge variant="muted" className="ml-2">{mappedCount} mapped</Badge>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={handleClose}>
              <X className="h-4 w-4" /> Cancel
            </Button>
            <Button variant="gold" onClick={handleUpload}>
              <Upload className="h-4 w-4" /> Start Import
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
